/**
 * The photo pipeline's shared rules (deck ruling k11, docs/20 §8, docs/24 §5, docs/41 step 6): what a photo shows,
 * why a photo can't be used, and how many photos a site needs before it can go live (c057). One definition, read by
 * the app (the library and the get live step), jobs (the pipeline, the onboarding Workflow and the nudge) and the
 * console (the review queue), so the three can't drift.
 */
import { z } from 'zod';
import { DB_ENUMS } from './enums.ts';

// ---------------------------------------------------------------------------------------------- what a photo shows

/**
 * What a client photo shows, in the client's words. `before` and `after` are finished work seen at two moments;
 * `vehicle` is their van or truck; `logo` is a brand file and never fills a photo slot on the site.
 */
export const PHOTO_KINDS = DB_ENUMS['media_assets.kind'];
export const PhotoKind = z.enum(PHOTO_KINDS);
export type PhotoKind = z.infer<typeof PhotoKind>;

/** Who said what a photo shows. A client's or staff's word beats a guess from the job it came with. */
export const PHOTO_KIND_SOURCES = DB_ENUMS['media_assets.kind_source'];
export const PhotoKindSource = z.enum(PHOTO_KIND_SOURCES);
export type PhotoKindSource = z.infer<typeof PhotoKindSource>;

/** The site contract's `depicts` (docs/20 §8.4, @ff/schemas MediaDepicts) for each kind. A logo fills no slot. */
export const PHOTO_KIND_DEPICTS: Readonly<Record<PhotoKind, 'work' | 'people' | 'premises' | null>> = {
  work: 'work',
  before: 'work',
  after: 'work',
  people: 'people',
  premises: 'premises',
  vehicle: 'premises',
  logo: null,
};

// ---------------------------------------------------------------------------------------------- why a photo is out

/**
 * Why a photo can't be used. The client sees a plain sentence for each (packages/app-ui copy `photos.problem`):
 * the pipeline sets the first six, the last three are a person's call in the console.
 */
export const PHOTO_PROBLEMS = ['not_a_photo', 'unreadable', 'too_large', 'too_small', 'too_dark', 'blurry', 'duplicate', 'private', 'not_yours', 'other'] as const;
export const PhotoProblem = z.enum(PHOTO_PROBLEMS);
export type PhotoProblem = z.infer<typeof PhotoProblem>;

/** The reasons staff may give when they turn a photo down (the pipeline's own reasons aren't theirs to pick). */
export const STAFF_PHOTO_PROBLEMS = ['too_small', 'too_dark', 'blurry', 'duplicate', 'private', 'not_yours', 'other'] as const satisfies readonly PhotoProblem[];
export const StaffPhotoProblem = z.enum(STAFF_PHOTO_PROBLEMS);

/**
 * The quality gates (docs/20 §8.1 "a sharpness and exposure score"). Sizes come from the site contract: a placed
 * photo is at least 600 wide and 400 tall (@ff/schemas MediaAsset), and 800 on its long edge fills the second
 * rendition width. Darkness and blur are measured on a 1,024 pixel copy. The thresholds were set against the 25
 * fixture photos (the softest scores 143 for sharpness, the darkest 73 for brightness) and the same photos blurred
 * and darkened (blurred: 2 to 20; darkened: 8 to 22), so only photos nobody would publish fail.
 */
export const PHOTO_GATES = {
  /** Of the upright clean copy, in pixels. */
  minWidth: 600,
  minHeight: 400,
  minLongEdge: 800,
  /** Decoded size cap: anything larger is refused before it's decoded in full (a decompression bomb, docs/53 T-9). */
  maxPixels: 100_000_000,
  /** Long edge of the copy the measurements are taken on. */
  sampleEdge: 1024,
  /** Mean luma (0 to 255) under this is too dark to use. */
  minBrightness: 28,
  /** Variance of the Laplacian over the sharpest tiles; under this nothing in the photo is in focus. */
  minSharpness: 30,
  /** Perceptual hashes this close (bits of 64) are the same photo sent twice. */
  duplicateDistance: 4,
} as const;

/**
 * The quality gates as one function. A dim photo has less contrast, so its edges measure weaker however sharp it
 * is: the sharpness bar drops with the square of brightness below 100, and a photo already too dark isn't also
 * called blurry.
 */
export function qualityProblems(m: { width: number; height: number; brightness: number; sharpness: number }): PhotoProblem[] {
  const out: PhotoProblem[] = [];
  if (m.width < PHOTO_GATES.minWidth || m.height < PHOTO_GATES.minHeight || Math.max(m.width, m.height) < PHOTO_GATES.minLongEdge) out.push('too_small');
  const dark = m.brightness < PHOTO_GATES.minBrightness;
  if (dark) out.push('too_dark');
  const bar = PHOTO_GATES.minSharpness * Math.min(1, (m.brightness / 100) ** 2);
  if (!dark && m.sharpness < bar) out.push('blurry');
  return out;
}

// ---------------------------------------------------------------------------------------------- enough to go live

/**
 * "Enough photos to go live" (c057), from the slots the site contract fills only with the client's own photos
 * (docs/20 §8.4): finished work for the hero and the service tiles, people for the about section, and their van or
 * premises for the call band. Stock never stands in for any of these on a live site, so without them the site would
 * open with typographic stand-ins where its proof should be. The shot list is docs/24 §5.5.
 */
export const GO_LIVE_PHOTO_RULE = { work: 3, people: 1, place: 1 } as const;
export type PhotoNeed = keyof typeof GO_LIVE_PHOTO_RULE;
export const PHOTO_NEEDS = Object.keys(GO_LIVE_PHOTO_RULE) as PhotoNeed[];
export const PhotoNeedKey = z.enum(['work', 'people', 'place']);

/** Which need a kind counts towards. A logo counts towards none. */
export function needOf(kind: PhotoKind | null | undefined): PhotoNeed | null {
  if (kind === 'work' || kind === 'before' || kind === 'after') return 'work';
  if (kind === 'people') return 'people';
  if (kind === 'premises' || kind === 'vehicle') return 'place';
  return null;
}

export const PhotoCounts = z.object({ work: z.number().int().min(0), people: z.number().int().min(0), place: z.number().int().min(0) });
export type PhotoCounts = z.infer<typeof PhotoCounts>;

export const PhotoMissing = z.object({ need: PhotoNeedKey, count: z.number().int().min(1).max(20) });
export type PhotoMissing = z.infer<typeof PhotoMissing>;

/**
 * Where a client stands against the rule.
 * - `sent`: photos they've sent that passed the checks and that nobody turned down (waiting for our look, flagged for
 *   a closer one, or approved).
 * - `approved`: of those, the ones a person here approved for the site.
 * - `missing`: what they still need to send, by need ("2 more photos of finished work"). Empty once they've sent enough.
 * - `met`: enough are approved; the photos step is done.
 * - `waitingOnUs`: they've sent enough and we haven't approved enough yet. The clock doesn't pause for that.
 */
export const PhotoNeeds = z.object({
  rule: PhotoCounts,
  sent: PhotoCounts,
  approved: PhotoCounts,
  missing: z.array(PhotoMissing).max(3),
  met: z.boolean(),
  waitingOnUs: z.boolean(),
  /** Photos still being checked by the pipeline: counted in nothing yet. */
  processing: z.number().int().min(0),
});
export type PhotoNeeds = z.infer<typeof PhotoNeeds>;

/** A photo as the rule sees it. */
export interface RulePhoto {
  kind: PhotoKind | null;
  /** `pending` and `flagged` wait for a person, `ok` is approved, `rejected` is out. */
  state: 'pending' | 'ok' | 'flagged' | 'rejected';
  /** False while the pipeline hasn't finished with it. */
  processed: boolean;
  /** "Don't use this one", or removed. */
  out: boolean;
}

const zero = (): PhotoCounts => ({ work: 0, people: 0, place: 0 });

/** The one rule. Pure: the same photos always give the same answer. */
export function photoNeeds(photos: readonly RulePhoto[], rule: PhotoCounts = GO_LIVE_PHOTO_RULE): PhotoNeeds {
  const sent = zero();
  const approved = zero();
  let processing = 0;
  for (const p of photos) {
    if (p.out) continue;
    if (!p.processed) {
      processing++;
      continue;
    }
    if (p.state === 'rejected') continue;
    const need = needOf(p.kind);
    if (!need) continue;
    sent[need]++;
    if (p.state === 'ok') approved[need]++;
  }
  const missing = PHOTO_NEEDS.filter((n) => sent[n] < rule[n]).map((n) => ({ need: n, count: rule[n] - sent[n] }));
  const met = PHOTO_NEEDS.every((n) => approved[n] >= rule[n]);
  return { rule: { ...rule }, sent, approved, missing, met, waitingOnUs: !met && missing.length === 0, processing };
}

/** The columns of a `media_assets` row the rule reads. */
export interface PhotoRuleRow {
  source: string;
  kind: PhotoKind | null;
  screen_state: RulePhoto['state'];
  processed_at: string | null;
  excluded: number;
  deleted_at: string | null;
}

/** Photos the client sent us (the app, their photo address, a picture message): never stock, never their Google profile's. */
export const CLIENT_PHOTO_SOURCES = ['app', 'email', 'mms'] as const;

/** The rule over library rows: only the client's own photos count, whatever else the library holds. */
export function photoNeedsOf(rows: readonly PhotoRuleRow[], rule: PhotoCounts = GO_LIVE_PHOTO_RULE): PhotoNeeds {
  return photoNeeds(
    rows.filter((r) => (CLIENT_PHOTO_SOURCES as readonly string[]).includes(r.source)).map((r) => ({ kind: r.kind, state: r.screen_state, processed: r.processed_at !== null, out: r.excluded === 1 || r.deleted_at !== null })),
    rule,
  );
}

// ---------------------------------------------------------------------------------------------- the pipeline's notes

/** What the pipeline measured, kept on the asset (`media_assets.quality_json`) and shown in the console. */
export const PhotoQuality = z.object({
  /** Mean luma, 0 to 255. */
  brightness: z.number().min(0).max(255),
  /** Variance of the Laplacian on the greyscale copy. */
  sharpness: z.number().min(0),
  /** `#rrggbb`, shown behind the photo until it paints (docs/20 §8.3). */
  dominant: z.string().regex(/^#[0-9a-f]{6}$/).meta({ examples: ['#5b6770'] }),
  /** The file as it arrived. */
  sourceFormat: z.enum(['jpeg', 'png', 'webp', 'heic']),
  sourceBytes: z.number().int().min(0),
});
export type PhotoQuality = z.infer<typeof PhotoQuality>;

/** `media_assets.screen_flags_json`: why a photo is out, and the words the client reads. */
export const PhotoFlags = z.object({
  reasons: z.array(PhotoProblem).max(6),
  /** Staff's own words to the client, when they turned it down. */
  note: z.string().max(160).nullable(),
  by: z.enum(['pipeline', 'staff']),
});
export type PhotoFlags = z.infer<typeof PhotoFlags>;

export const Focal = z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) });
export type Focal = z.infer<typeof Focal>;

/** A site photo slot a person can put a photo in (docs/24 §5.2, @ff/schemas Photos), or one service's photo. */
export const PAGE_PHOTO_SLOTS = ['hero', 'about', 'cta', 'financing', 'coverage'] as const;
export const PagePhotoSlot = z.enum(PAGE_PHOTO_SLOTS);
export type PagePhotoSlot = z.infer<typeof PagePhotoSlot>;

/** What each page slot may show on a live site (docs/20 §8.4, docs/24 §5.1): never a logo, and the about slot is people. */
export const SLOT_KINDS: Readonly<Record<PagePhotoSlot | 'service', readonly PhotoKind[]>> = {
  hero: ['work', 'after', 'people', 'vehicle', 'premises'],
  about: ['people', 'premises', 'vehicle'],
  cta: ['vehicle', 'premises', 'people', 'work', 'after'],
  financing: ['work', 'after', 'people', 'premises', 'vehicle'],
  coverage: ['vehicle', 'premises', 'work', 'after'],
  service: ['work', 'after', 'before'],
};

/** The cache-busting part of a rendition's folder: the same photo with a new focal point gets new URLs. */
export function renditionRev(sha256: string, focal: Focal): string {
  const fx = Math.round(focal.x * 255).toString(16).padStart(2, '0');
  const fy = Math.round(focal.y * 255).toString(16).padStart(2, '0');
  return `${sha256.slice(0, 4)}${fx}${fy}`.toLowerCase();
}
